import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { get } from 'lodash';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import CSP from './comparison_shopping_page';
import { NameMetaData, BrandMetaData, PriceMetaData } from './metadata_helper';

import bind from '@reverbdotcom/commons/src/bind';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { RCImage, RCSkeleton } from '@reverbdotcom/cadence/components';

interface Props extends IUserContext {
  page?: CSP;
  position?: number;
  trackName?: string;
  trackContext?: object;
  skipTracking?: boolean;
  stripBrandFromTitle?: boolean;
  conditionFilter?: ConditionFilter;
  hideMetadata?: boolean;
  renderAsHorizontal?: boolean;
}

type ConditionFilter = 'new' | 'used';

export class CSPSquareCard extends React.Component<Props, null> {
  static get defaultProps(): Partial<Props> {
    return {
      trackName: 'CSPSquareCard',
      trackContext: {},
    };
  }

  @bind
  trackClick() {
    if (!this.props.page?.slug) {
      return;
    }
    if (this.props.skipTracking) {
      return;
    }

    const { page, position, trackName } = this.props;

    trackEvent({
      position,
      componentName: trackName,
      eventName: MParticleEventName.ClickedCSPCard,
      cspId: page.id,
      cspSlug: page.slug,
      brand: '', // TODO- currently don't have this data in v3 response
      category: page.category,
      subcategories: '',
      displayStyle: 'CSPSquareCardV3',
    });
  }

  titleWithBrandRemoved() {
    if (!this.props.page) {
      return null;
    }

    if (!this.props.stripBrandFromTitle) {
      return this.props.page.title;
    }

    const brandName = get(this.props, 'page.brand.name');
    if (!brandName) {
      return this.props.page.title;
    }

    const regex = new RegExp(`^${brandName} `, 'i');
    return this.props.page.title.replace(regex, '');
  }

  priceAndCount() {
    if (this.props.conditionFilter === 'new') {
      const { newLowPrice, newListingsCount } = this.props.page;
      return { count: newListingsCount, price: newLowPrice };
    }

    if (this.props.conditionFilter === 'used') {
      const { usedLowPrice, usedListingsCount } = this.props.page;
      return { count: usedListingsCount, price: usedLowPrice };
    }

    const count =
      this.props.page.usedListingsCount + this.props.page.newListingsCount;
    const price = this.props.page.lowestPrice();
    return { count, price };
  }

  renderCalloutLanguage() {
    if (!this.props.page) {
      return null;
    }

    const { price, count } = this.priceAndCount();

    if (!price) { return null; }

    if (price.amount_cents === 0) {
      return I18n.t(
        'discovery.comparisonShoppingPages.priceForFreeHighlightedCount',
        { count, price: price.display, tagOpen: '<b>', tagClose: '</b>' },
      );
    }

    return I18n.t(
      'discovery.comparisonShoppingPages.priceFromWithHighlightedCount',
      { count, price: price.display, tagOpen: '<b>', tagClose: '</b>' },
    );
  }

  renderPlaceholderCard() {
    return (
      <div className="csp-square-card csp-square-card--placeholder">
        <div className="csp-square-card__inner">
          <div className="csp-square-card__main">
            <RCSkeleton shape="square" />
            <div className="csp-square-card__title">
              <RCSkeleton shape="text" width="80%" />
            </div>
          </div>
          <div className="csp-square-card__details" />
        </div>
      </div>
    );
  }

  renderPriceMetadata() {
    const inventory = {
      usedLowPrice: this.props.page.usedLowPrice,
      usedTotal: this.props.page.usedListingsCount,
      newLowPrice: this.props.page.newLowPrice,
      newTotal: this.props.page.newListingsCount,
    };

    if (this.props.conditionFilter === 'new') {
      return <PriceMetaData newOnly inventory={inventory} />;
    }

    if (this.props.conditionFilter === 'used') {
      return <PriceMetaData usedOnly inventory={inventory} />;
    }

    return <PriceMetaData inventory={inventory} />;
  }

  webLink() {
    return this.props.page.webLink();
  }

  renderTitle() {
    return (
      <h3 className="csp-square-card__title">
        {this.titleWithBrandRemoved()}
      </h3>
    );
  }

  render() {
    const classes = classNames(
      'csp-square-card',
      { 'csp-square-card--horizontal': this.props.renderAsHorizontal },
    );

    if (!this.props.page) {
      return this.renderPlaceholderCard();
    }

    if (this.props.hideMetadata) {
      return (
        /* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
        (<article
          className={classes}
          onClick={this.trackClick}
        >
          <a
            href={this.webLink()}
            className="csp-square-card__inner"
          >
            <div className="csp-square-card__main">
              <RCImage
                src={this.props.page.gridImageHref()}
              />
              {!this.props.renderAsHorizontal && this.renderTitle()}
            </div>
            <div
              className="csp-square-card__details"
            >
              {this.props.renderAsHorizontal && this.renderTitle()}
              <div className="csp-square-card__details__price">
                <SanitizedRender html={this.renderCalloutLanguage()} />
              </div>
            </div>
          </a>
        </article>)
      );
    }

    return (
      /* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
      (<article
        className={classes}
        onClick={this.trackClick}
        itemScope
        itemType="http://schema.org/Product"
      >
        <CoreLink
          to={this.webLink()}
          className="csp-square-card__inner"
        >
          <div className="csp-square-card__main">
            <RCImage
              src={this.props.page.gridImageHref()}
            />
            <BrandMetaData brandName={get(this.props, 'page.brand.name')} />
            <NameMetaData title={this.props.page.title} />
            {!this.props.renderAsHorizontal &&
              <h3 className="csp-square-card__title" itemProp="model">
                {this.titleWithBrandRemoved()}
              </h3>
            }
          </div>
          <div
            className="csp-square-card__details"
            itemScope
            itemProp="offers"
            itemType="http://schema.org/AggregateOffer"
          >
            {this.props.renderAsHorizontal &&
              <h3 className="csp-square-card__title" itemProp="model">
                {this.titleWithBrandRemoved()}
              </h3>
            }
            <div className="csp-square-card__details__price">
              {this.renderPriceMetadata()}
              <SanitizedRender html={this.renderCalloutLanguage()} />
            </div>
          </div>
        </CoreLink>
      </article>)
    );
  }
}

export default withUserContext(CSPSquareCard);
